/**
 * Prompt Context Card Component
 * Shared mini-card for "Response to" / "Responding to" prompt context.
 * Used in SinglePostDisplay (posts), WriteNewPostForm (create-post), and the
 * day archive page. The date links to the prompt's /day/:n page (the page that
 * owns "WriteFlow #N") so day archives are one tap away and crawlable.
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { formatPromptDate } from '../../utils/formatPromptDate';
import { dateToDayNumber } from '../../utils/promptDayNumber';
import './PromptContextCard.css';

const PromptContextCard = ({ prompt, badge = 'Response to', linkToDay = true }) => {
  const [promptImageError, setPromptImageError] = useState(false);
  const displayPromptImageUrl = prompt?.imageUrl;
  const showPromptPlaceholder = !displayPromptImageUrl || promptImageError;

  useEffect(() => {
    setPromptImageError(false);
  }, [prompt?._id, prompt?.imageUrl]);

  if (!prompt) return null;

  const dayNumber = prompt.date ? dateToDayNumber(prompt.date) : null;
  const dateLabel = prompt.date ? formatPromptDate(prompt.date) : null;

  return (
    <div className="post-prompt-context">
      <div className="post-prompt-header">
        <span className="post-prompt-badge">{badge}</span>
        {dateLabel && (
          linkToDay && dayNumber ? (
            <Link
              to={`/day/${dayNumber}`}
              className="post-prompt-date"
              title={`WriteFlow #${dayNumber} — everything written this day`}
              onClick={(e) => e.stopPropagation()}
            >
              #{dayNumber} · {dateLabel}
            </Link>
          ) : (
            <span className="post-prompt-date">{dateLabel}</span>
          )
        )}
      </div>
      <div className="post-prompt-body">
        {showPromptPlaceholder ? (
          <div className="post-prompt-image-placeholder" aria-hidden="true" />
        ) : (
          <img
            src={displayPromptImageUrl}
            alt=""
            className="post-prompt-image"
            onError={() => setPromptImageError(true)}
          />
        )}
        <div className="post-prompt-body-overlay">
          <p className="post-prompt-overlay-emotion">{prompt.emotion}</p>
          <p className="post-prompt-overlay-connector">{prompt.connector}</p>
          <p className="post-prompt-overlay-setting">{prompt.setting}</p>
        </div>
      </div>
    </div>
  );
};

export default PromptContextCard;
